import { initializeApp, getApp, getApps } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

const PRODUCTION_PROJECT_ID = "voorraadsysteem-bbf99";

const requiredFirebaseEnvKeys = [
  "REACT_APP_FIREBASE_API_KEY",
  "REACT_APP_FIREBASE_AUTH_DOMAIN",
  "REACT_APP_FIREBASE_PROJECT_ID",
  "REACT_APP_FIREBASE_STORAGE_BUCKET",
  "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
  "REACT_APP_FIREBASE_APP_ID",
];

const getAppEnvironment = () =>
  process.env.REACT_APP_APP_ENV ||
  (process.env.NODE_ENV === "production" ? "production" : "development");

const requireFirebaseConfig = () => {
  const missingKeys = requiredFirebaseEnvKeys.filter((key) => !process.env[key]);

  if (missingKeys.length > 0) {
    throw new Error(
      `Firebase config ontbreekt: ${missingKeys.join(", ")}. ` +
        "Maak een .env.local/.env.development.local voor development en .env.production.local voor productie.",
    );
  }

  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
};

const assertSafeFirebaseTarget = (config) => {
  const appEnvironment = getAppEnvironment();
  const usesProductionProject = config.projectId === PRODUCTION_PROJECT_ID;
  const allowsProductionInNonProduction =
    process.env.REACT_APP_ALLOW_PROD_FIREBASE_IN_NON_PROD === "true";

  if (
    process.env.NODE_ENV !== "production" &&
    usesProductionProject &&
    !allowsProductionInNonProduction
  ) {
    throw new Error(
      `Development is gekoppeld aan productieproject ${PRODUCTION_PROJECT_ID}. ` +
        "Gebruik een dev/staging Firebase project of zet REACT_APP_ALLOW_PROD_FIREBASE_IN_NON_PROD=true bewust aan.",
    );
  }

  if (appEnvironment !== "production" && usesProductionProject) {
    console.warn(
      `Firebase gebruikt productieproject ${PRODUCTION_PROJECT_ID} terwijl REACT_APP_APP_ENV=${appEnvironment}.`,
    );
  }
};

const firebaseConfig = requireFirebaseConfig();
assertSafeFirebaseTarget(firebaseConfig);

export const firebaseAppEnvironment = getAppEnvironment();
export const firebaseProjectId = firebaseConfig.projectId;

export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const initializeFirebaseAppCheck = () => {
  const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
  if (!siteKey) {
    if (firebaseAppEnvironment === "production") {
      throw new Error(
        "REACT_APP_FIREBASE_APPCHECK_SITE_KEY ontbreekt in de productiebuild.",
      );
    }
    return null;
  }

  const debugEnabled =
    process.env.NODE_ENV !== "production" &&
    process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG === "true";
  if (debugEnabled && typeof window !== "undefined") {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  return initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
};

export const appCheck = initializeFirebaseAppCheck();
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION
  ? getFunctions(app, process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION)
  : getFunctions(app);
